import cloneDeep from 'lodash.clonedeep';

import { BIRTHDATE_FIELDNAME } from '../../components/generic/form/InputBirthDate';
import { EMAIL_FIELDNAME } from '../../components/generic/form/InputEmail';
import { PHONE_COUNTRYCODE_FIELDNAME, PHONE_FIELDNAME } from '../../components/generic/form/InputPhone';
import { FIRST_NAME_FIELDNAME, LAST_NAME_FIELDNAME, SEX_FIELDNAME } from '../../hooks/useReadAdditionalData';
import { findArrayItemById } from '../arrayUtil';
import { toIsoDate } from '../dateUtil';
import { adaptPhoneForBackend } from '../phoneUtil';
import { getNormalizedFullName, trimObjectKeys } from '../strUtil';

export const handleParticipantDataForBackend = participant => {
  const participantCopy = cloneDeep(participant);
  delete participantCopy.displayed;

  if ('birth_date' in participantCopy) {
    participantCopy['birth_date'] = toIsoDate(participantCopy['birth_date']);
  }

  // backend cannot accept empty, react cannot accept null
  if (SEX_FIELDNAME in participantCopy && participantCopy[SEX_FIELDNAME] === '') {
    participantCopy[SEX_FIELDNAME] = null;
  }

  trimObjectKeys(participantCopy, [FIRST_NAME_FIELDNAME, LAST_NAME_FIELDNAME, EMAIL_FIELDNAME]);

  participantCopy[PHONE_FIELDNAME] = adaptPhoneForBackend(participantCopy[PHONE_FIELDNAME], participantCopy[PHONE_COUNTRYCODE_FIELDNAME]);
  delete participantCopy[PHONE_COUNTRYCODE_FIELDNAME];

  // In case of a select, we need to send the label, not the id, that's why we do that replace
  if (participantCopy['extra_data_select']) {
    for (const [key, value] of Object.entries(participantCopy['extra_data_select'])) {
      participantCopy['extra_data'][key] = value;
    }
    delete participantCopy.extra_data_select;
  }
  return participantCopy;
};

export const handleParticipantDataForFrontEnd = participant => {
  const participantCopy = cloneDeep(participant);
  if (participantCopy['birth_date']) {
    participantCopy['birth_date'] = toIsoDate(participantCopy['birth_date']);
  }
  return participantCopy;
};

// Builds a map of sessionGroupId -> Set of normalized participant names already in the cart.
// itemIdsToExclude lets the edit flow ignore the cart items that are about to be replaced.
const getCartParticipantNamesBySession = (cartData, itemIdsToExclude) => {
  const namesBySession = new Map();
  if (!cartData || !Array.isArray(cartData.ocSessionsWithResources)) return namesBySession;

  const excluded = new Set(itemIdsToExclude ?? []);
  for (const [, group] of cartData.ocSessionsWithResources) {
    if (group.sessionGroupId == null) continue;

    let names = namesBySession.get(group.sessionGroupId);
    if (!names) {
      names = new Set();
      namesBySession.set(group.sessionGroupId, names);
    }

    for (const item of group.availabilities) {
      if (excluded.has(item.id)) continue;

      const client = findArrayItemById(item.client_id, cartData.clients);
      if (!client) continue;

      const fullName = getNormalizedFullName(client.first_name, client.last_name);
      if (fullName !== '') names.add(fullName);
    }
  }
  return namesBySession;
};

// Returns true if a participant being added (preCartData) is already booked in the cart
// for the exact same scheduled session (session_group_id). Matching is by normalized name
// because the new participants have no client_id yet.
export const hasParticipantAlreadyInCart = (preCartData, cartData, itemIdsToExclude) => {
  if (!preCartData || !Array.isArray(preCartData.availabilities)) return false;

  const namesBySession = getCartParticipantNamesBySession(cartData, itemIdsToExclude);
  if (namesBySession.size === 0) return false;

  for (const availability of preCartData.availabilities) {
    const existingNames = namesBySession.get(availability.session_group_id);
    if (!existingNames || existingNames.size === 0) continue;

    for (const variant of availability.variants ?? []) {
      const client = variant.participantDetails?.client;
      if (!client) continue;

      const fullName = getNormalizedFullName(client.first_name, client.last_name);
      if (fullName !== '' && existingNames.has(fullName)) return true;
    }
  }
  return false;
};

// small hack for the clients fields bc the birthdate doesnt match its specific data name
export const getCorrectClientFieldName = label => (label === BIRTHDATE_FIELDNAME ? 'birth_date' : label);

export const updateParticipantData = (copyParticipant, customDataId, label, newValue, additionalData) => {
  if (customDataId !== null) {
    copyParticipant.extra_data[label] = newValue;
    if (additionalData) {
      if (!copyParticipant.extra_data_select) {
        copyParticipant.extra_data_select = {};
      }
      copyParticipant.extra_data_select[label] = additionalData;
    }
    return;
  }

  const validLabel = getCorrectClientFieldName(label);
  copyParticipant[validLabel] = newValue;

  if (validLabel === PHONE_FIELDNAME) {
    copyParticipant[PHONE_COUNTRYCODE_FIELDNAME] = additionalData;
  }
};
